import React, { useState, useEffect } from "react";
import Card from "components/card";
import { MdWarehouse, MdInventory, MdCategory, MdTimer, MdClose, MdBarChart, MdTimeline, MdError, MdWarning, MdVisibility } from "react-icons/md";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import ProductDistributionPieChart from "./components/ProductDistributionPieChart";
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const FinishedDashboard = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Fetch data from API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await axios.get('https://usibras-web-backend.onrender.com/api/fetch-finished-products');
        if (response.data && response.data.data) {
          setProducts(response.data.data);
        }
      } catch (err) {
        console.error('Error:', err);
        setError('Error loading products');
      } finally {
        setLoading(false);
      }
    };
    
    fetchProducts();
  }, []);

  // Calculate summary metrics
  const summaryMetrics = {
    totalProducts: products.length,
    totalQuantity: products.reduce((acc, curr) => acc + parseInt(curr.actual_stock), 0),
    averageAge: Math.round(
      products.reduce((acc, curr) => {
        const batchAge = curr.batch_details[0]?.age || 0;
        return acc + batchAge;
      }, 0) / products.length
    ),
    productsOverSixMonths: products.filter(product => 
      (product.batch_details[0]?.age || 0) > 180
    ).length
  };

  // Prepare data for product distribution chart
  const productDistributionData = products.map(product => ({
    name: product.product_type,
    quantity: parseInt(product.actual_stock)
  }));

  // Prepare data for age distribution chart
  const ageDistributionData = products.map(product => ({
    name: product.product_type,
    days: product.batch_details[0]?.age || 0
  }));

  // Update the Age Information section in the modal
  const renderAgeInformation = (product) => {
    const batchDetails = product.batch_details[0];
    return (
      <div className="bg-gray-50 p-4 rounded-lg md:col-span-2">
        <div className="flex items-center space-x-2 mb-3">
          <MdTimer className="text-purple-600" size={24} />
          <span className="font-medium text-lg">Age Information</span>
        </div>
        <div className="flex justify-center">
          <div className="text-center">
            <p className="text-sm text-gray-500">Days in Warehouse</p>
            <p className="text-3xl font-semibold text-purple-600">{batchDetails?.age || 0}</p>
            <p className="text-sm text-gray-500 mt-1">Since {new Date(batchDetails?.date).toLocaleDateString()}</p>
          </div>
        </div>
      </div>
    );
  };

  const renderCharts = () => {
    // Filter products with actual stock over 700
    const highQuantityProducts = products.filter(
      product => Math.abs(parseInt(product.actual_stock)) >= 700
    );

    return (
      <div className="grid grid-cols-1 gap-6 mb-8">
        {/* Product Distribution Pie Chart - Full Width */}
        <div className="col-span-1">
          <ProductDistributionPieChart products={products} />
        </div>

        {/* Alerts Section */}
        <div className="col-span-1">
          <Card extra="p-6">
            <div className="flex items-center space-x-2 mb-4">
              <MdWarning className="text-yellow-600" size={24} />
              <h3 className="text-xl font-bold text-gray-800">Stock Alerts (Over 700 boxes)</h3>
            </div>
            
            <div className="space-y-4">
              {highQuantityProducts.map((product) => {
                const batchDetail = product.batch_details[0];
                const actualStock = parseInt(product.actual_stock);
                const percentage = Math.min((Math.abs(actualStock) / 1000) * 100, 100);

                return (
                  <div key={product.product_type} className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-lg">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <div className="bg-yellow-100 p-2 rounded-full">
                          <MdWarning className="text-yellow-600" size={20} />
                        </div>
                      </div>
                      <div className="ml-3 w-full">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="text-lg font-semibold text-gray-900">{product.product_type}</h4>
                            <p className="mt-1 text-sm text-gray-600">
                              Current Stock: <span className="font-medium">{actualStock.toLocaleString()} boxes</span>
                            </p>
                          </div>
                          <button
                            onClick={() => navigate(`/admin/finished-product-warehouse/${encodeURIComponent(product.product_type)}`)}
                            className="flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-100"
                          >
                            <MdVisibility className="h-5 w-5" />
                            View Details
                          </button>
                        </div>
                        <div className="mt-3">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-yellow-500 h-2 rounded-full"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                          <p className="mt-1 text-xs text-gray-500 text-right">
                            {Math.abs(actualStock).toLocaleString()} / 1000 boxes
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <div className="relative">
          <div className="h-24 w-24 rounded-full border-t-4 border-b-4 border-brand-500"></div>
          <div className="absolute top-0 left-0 h-24 w-24 animate-spin rounded-full border-t-4 border-b-4 border-gray-200"></div>
          <div className="absolute top-1/2 left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-500"></div>
          <div className="mt-8 text-center">
            <p className="text-lg font-medium text-gray-600 dark:text-white">Loading</p>
            <p className="text-sm text-gray-400">Please wait...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <div className="text-center">
          <div className="mb-4 text-5xl text-red-500">
            <MdError className="mx-auto h-16 w-16" />
          </div>
          <p className="text-xl font-semibold text-gray-700 dark:text-white">
            Oops! Something went wrong
          </p>
          <p className="mt-2 text-gray-500 dark:text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3">
      <div className="flex flex-col gap-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-100 p-3 rounded-lg">
              <MdWarehouse className="text-blue-600" size={24} />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Warehouse Overview</h2>
              <p className="text-sm text-gray-600">View and manage your warehouse inventory</p>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Products Card */}
          <Card extra="p-4">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-100 p-3 rounded-lg">
                <MdCategory className="text-blue-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Product Types</p>
                <h3 className="text-xl font-bold text-gray-800">{summaryMetrics.totalProducts}</h3>
              </div>
            </div>
          </Card>

          {/* Total Quantity Card */}
          <Card extra="p-4">
            <div className="flex items-center space-x-3">
              <div className="bg-green-100 p-3 rounded-lg">
                <MdInventory className="text-green-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Actual Stock</p>
                <h3 className="text-xl font-bold text-gray-800">{summaryMetrics.totalQuantity.toLocaleString()}</h3>
              </div>
            </div>
          </Card>

          {/* Products Over 6 Months Card */}
          <Card extra="p-4">
            <div className="flex items-center space-x-3">
              <div className="bg-purple-100 p-3 rounded-lg">
                <MdTimer className="text-purple-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-600">Products Over 6 Months</p>
                <h3 className="text-xl font-bold text-gray-800">{summaryMetrics.productsOverSixMonths}</h3>
              </div>
            </div>
          </Card>

          {/* Average Age Card */}
          <Card extra="p-4">
            <div className="flex items-center space-x-3">
              <div className="bg-orange-100 p-3 rounded-lg">
                <MdTimer className="text-orange-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-600">Average Age</p>
                <h3 className="text-xl font-bold text-gray-800">{summaryMetrics.averageAge} days</h3>
              </div>
            </div>
          </Card>
        </div>

        {/* Charts */}
        {renderCharts()}

        {/* Product Details Modal */}
        {selectedProduct && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" onClick={() => setSelectedProduct(null)}></div>
            
            {/* Modal */}
            <div className="flex min-h-full items-center justify-center p-4">
              <Card extra="p-6 max-w-2xl w-full relative">
                {/* Close Button */}
                <button
                  onClick={() => setSelectedProduct(null)}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <MdClose size={24} />
                </button>

                {/* Header */}
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-gray-800">{selectedProduct.product_type}</h3>
                  <p className="text-sm text-gray-500">Product Details</p>
                </div>

                {/* Content */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Quantity Information */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center space-x-2 mb-3">
                      <MdInventory className="text-green-600" size={24} />
                      <span className="font-medium text-lg">Stock Information</span>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Product Type Stock</p>
                      <p className="text-xl font-semibold">{selectedProduct.actual_stock} boxes</p>
                      <p className="text-sm text-gray-500 mt-2">Batch Stock</p>
                      <p className="text-xl font-semibold">{selectedProduct.batch_details[0]?.batch_actual_stock} boxes</p>
                    </div>
                  </div>

                  {/* Age Information */}
                  {renderAgeInformation(selectedProduct)}
                </div>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FinishedDashboard; 